import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { createRoot } from 'react-dom/client';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';
import { useApi } from '../../context/ApiProvider/useApi'; // Added useApi import
import Bookmark from '../Bookmark/Bookmark';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Icon } from '../Icons/icons';
import type { AmenityPopup, Event, ViewMode } from '../../context/AppStateProvider/types';
import AmenityFilter from '../AmenityFilter/AmenityFilter';
import { SaveParkingButton } from '../Parking/Parking';

const PERTH_OFFSET = '+08:00';

function toPerthTimestamp(event: Event): number {
    // Use today's date in Perth if date is null
    const datePart = event.date
        ? event.date.split(' ')[0] // "YYYY-MM-DD"
        : new Date().toISOString().slice(0, 10);

    const timePart = event.start_time || '00:00:00';

    // Construct an ISO 8601 string with explicit timezone
    const iso = `${datePart}T${timePart}${PERTH_OFFSET}`;

    return new Date(iso).getTime();
}

function Map() {
    const mapDiv = useRef<HTMLDivElement>(null);
    const map = useRef<Mazemap.Map | null>(null);
    const marker = useRef<Mazemap.MazeMarker | null>(null);
    const markersRef = useRef<Mazemap.MazeMarker[]>([]);
    const amenityMarkersRef = useRef<Mazemap.MazeMarker[]>([]);
    const amenityMarkerRootsRef = useRef<Array<ReturnType<typeof createRoot>>>([]);
    const popup = useRef<Mazemap.Popup | null>(null);
    const prevActiveEventId = useRef<number | undefined>(undefined);
    const popupRoot = useRef<ReturnType<typeof createRoot> | null>(null);
    const prevViewModeRef = useRef<ViewMode | null>(null);
    const { state } = useAppState();
    const {
        activeEvent,
        viewMode,
        filteredEvents,
        mapExpanded,
        splitRatio,
        amenities,
        user,
        itinerary,
        activeAmenity,
    } = state;
    const api = useApi();
    const actions = useAppDispatchActions();
    const { setActiveEvent, setMapExpanded, setSplitRatio, setActiveAmenity } = actions;
    const { amenityTypes, selectedAmenityTypes } = state;
    const [amenityPopup, setAmenityPopup] = useState<AmenityPopup | null>(null);
    const amenityPopupRef = useRef<Mazemap.Popup | null>(null);
    const amenityPopupRoot = useRef<ReturnType<typeof createRoot> | null>(null);
    const filteredEventsRef = useRef(filteredEvents);
    const [popupEvents, setPopupEvents] = useState<any[]>([]);
    const [mazemapLoaded, setMazemapLoaded] = useState(false);
    const [mazemapInitialised, setMazemapInitialised] = useState(false);
    const OVERLAP_THRESHOLD = 0.00005; // Approx 5 meters

    useEffect(() => {
        filteredEventsRef.current = filteredEvents;
    }, [filteredEvents]);

    const mazemapFetcher = async (url: string) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch POIs');
        return res.json();
    };

    // Prepare POI type IDs to fetch
    const poiTypeIds = useMemo(() => {
        const ids = new Set<number>();
        selectedAmenityTypes.forEach((slug) => {
            const type = amenityTypes.find((t) => t.slug === slug);
            if (type?.poi) {
                type.poi.forEach((id) => ids.add(id));
            }
        });
        return Array.from(ids);
    }, [selectedAmenityTypes, amenityTypes]);

    const poiTypeIdsKey = useMemo(() => {
        if (poiTypeIds.length === 0) return null;
        return poiTypeIds.slice().sort((a, b) => a - b).join(',');
    }, [poiTypeIds]);

    // Fetch Mazemap POIs for each typeId
    // Since we can't call hooks in a loop, we fetch them all in one fetcher if we want to use SWR,
    // or we can use a custom logic. To follow the "useSWR" request, we'll use a composite key.
    const { data: poiDataArray } = useSWR(
        poiTypeIdsKey ? `mazemap-pois-${poiTypeIdsKey}` : null,
        async () => {
            const results = await Promise.all(
                poiTypeIds.map((id) =>
                    mazemapFetcher(`https://api.mazemap.com/api/campus/296/pois/?typeid=${id}&fromid=0&srid=4326`),
                ),
            );
            return results;
        },
        {
            revalidateOnFocus: false,
            keepPreviousData: true,
            dedupingInterval: 5 * 60 * 1000,
        },
    );

    const debounceTimeoutRef = useRef<number | null>(null);
    const closePopupTimeoutRef = useRef<number | null>(null);
    const amenityPopupOpenTimeoutRef = useRef<number | null>(null);
    const activeAmenityTimeoutRef = useRef<number | null>(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startRatio = useRef(50);
    const rafId = useRef<number | null>(null);

    const fallbackRender = useCallback(({ error, resetErrorBoundary }: FallbackProps) => {
        return (
            <div role='alert'>
                <p>Something went wrong:</p>
                <pre style={{ color: 'red' }}>{error instanceof Error ? error.message : 'Unknown error'}</pre>
                <button onClick={resetErrorBoundary}>Try again</button>
            </div>
        );
    }, []);

    const closePopup = () => {
        if (closePopupTimeoutRef.current) {
            clearTimeout(closePopupTimeoutRef.current);
        }
        closePopupTimeoutRef.current = window.setTimeout(() => {
            if (!popup.current?.isOpen()) {
                setActiveEvent(null);
            }
        }, 500);
    };

    useEffect(() => {
        if (mazemapLoaded) return;

        if (!window.Mazemap) {
            const script = document.createElement('script');
            script.src = 'https://api.mazemap.com/js/v2.2.5/mazemap.min.js';
            script.async = true;
            let cancelled = false;
            script.onload = () => {
                if (!cancelled) {
                    setMazemapLoaded(true);
                }
            };
            document.head.appendChild(script);

            return () => {
                cancelled = true;
            };
        }
    }, []);

    useEffect(() => {
        if (!mapDiv.current || map.current || !mazemapLoaded) return;

        const initialCenter = {
            lng: 115.891513708908,
            lat: -32.0041022812316,
        };

        map.current = new (window as any).Mazemap.Map({
            container: mapDiv.current,
            campuses: [296],
            center: initialCenter,
            zoom: 15,
            zLevel: 1,
            zLevelUpdater: true,
            zLevelControl: false,
            attributionControl: false,
            logoPosition: 'top-left',
        });

        map.current?.addControl(
            new (window as any).Mazemap.mapboxgl.AttributionControl({
                compact: true,
            }),
            'top-left',
        );

        // Add Zoom and Rotation controls
        map.current?.addControl(new (window as any).Mazemap.mapboxgl.NavigationControl(), 'bottom-right');

        // Add Geolocate control
        map.current?.addControl(
            new (window as any).Mazemap.mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                trackUserLocation: true,
                showUserHeading: true,
            }),
            'bottom-right',
        );

        map.current?.on('load', () => {
            if (!map.current) return;

            // Add events source with clustering
            map.current.addSource('events', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
                cluster: true,
                clusterMaxZoom: 17,
                clusterRadius: 50,
            });

            // Clusters layer
            map.current.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'events',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': '#0B0B0B',
                    'circle-opacity': 0.7,
                    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff',
                },
            });

            // Cluster count layer
            map.current.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'events',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                },
                paint: {
                    'text-color': '#ffffff',
                },
            });

            // Individual event points layer
            map.current.addLayer({
                id: 'unclustered-point',
                type: 'circle',
                source: 'events',
                filter: ['!has', 'point_count'],
                paint: {
                    'circle-color': ['get', 'colour'],
                    'circle-radius': 8,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff',
                },
            });

            // Click on cluster
            map.current.on('click', 'clusters', (e) => {
                if (!map.current) return;
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['clusters'],
                });
                const clusterId = features[0].properties?.cluster_id;
                const source = map.current.getSource('events') as mapboxgl.GeoJSONSource;
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err || !map.current) return;
                    map.current.easeTo({
                        center: (features[0].geometry as any).coordinates,
                        zoom: zoom,
                    });
                });
            });

            // Click on unclustered point
            map.current.on('click', 'unclustered-point', (e) => {
                if (!map.current) return;
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['unclustered-point'],
                });
                if (features.length > 0) {
                    const eventId = features[0].properties?.id;
                    const event = filteredEventsRef.current.find((ev) => ev.id === eventId);
                    if (event) {
                        setActiveEvent(event);
                    }
                }
            });

            // Hover state
            map.current.on('mouseenter', 'clusters', () => {
                if (map.current) map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'clusters', () => {
                if (map.current) map.current.getCanvas().style.cursor = '';
            });
            map.current.on('mouseenter', 'unclustered-point', () => {
                if (map.current) map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'unclustered-point', () => {
                if (map.current) map.current.getCanvas().style.cursor = '';
            });

            setMazemapInitialised(true);
        });

        return () => {
            console.log('map remove');

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            if (closePopupTimeoutRef.current) {
                clearTimeout(closePopupTimeoutRef.current);
                closePopupTimeoutRef.current = null;
            }
            if (amenityPopupOpenTimeoutRef.current) {
                clearTimeout(amenityPopupOpenTimeoutRef.current);
                amenityPopupOpenTimeoutRef.current = null;
            }
            if (activeAmenityTimeoutRef.current) {
                clearTimeout(activeAmenityTimeoutRef.current);
                activeAmenityTimeoutRef.current = null;
            }

            if (popupRoot.current) {
                popupRoot.current.unmount();
                popupRoot.current = null;
            }

            if (popup.current) {
                popup.current.remove();
                popup.current = null;
            }

            if (marker.current) {
                marker.current.remove();
                marker.current = null;
            }

            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];

            amenityMarkersRef.current.forEach((m) => m.remove());
            amenityMarkersRef.current = [];
            amenityMarkerRootsRef.current.forEach((r) => r.unmount());
            amenityMarkerRootsRef.current = [];

            if (map.current) {
                map.current.remove();
                map.current = null;
            }

            setMazemapInitialised(false);
        };
    }, [mazemapLoaded]);

    const renderPopupContent = useCallback(() => {
        const isMobile = window.innerWidth < 768;
        const currentIndex = popupEvents.findIndex((e) => e.id === activeEvent?.id);
        const totalEvents = popupEvents.length;

        const handleNext = () => {
            const nextIndex = (currentIndex + 1) % totalEvents;
            setActiveEvent(popupEvents[nextIndex]);
        };

        const handlePrev = () => {
            const prevIndex = (currentIndex - 1 + totalEvents) % totalEvents;
            setActiveEvent(popupEvents[prevIndex]);
        };

        return (
            <ErrorBoundary fallbackRender={fallbackRender}>
                <div className='text-left text-sm'>
                    {totalEvents > 1 && (
                        <div className='mb-2 flex items-center justify-between border-b border-gray-200 pr-8 pb-2'>
                            <span className='text-sm font-semibold text-gray-500'>
                                Event {currentIndex + 1} of {totalEvents}
                            </span>
                            <div className='flex gap-2'>
                                <button
                                    onClick={handlePrev}
                                    className='flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200'>
                                    <svg
                                        viewBox='0 0 32 32'
                                        fill='currentColor'
                                        width='20'
                                        height='20'>
                                        <polygon points='10 16 20 6 21.4 7.4 12.8 16 21.4 24.6 20 26' />
                                    </svg>
                                </button>
                                <button
                                    onClick={handleNext}
                                    className='flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200'>
                                    <svg
                                        viewBox='0 0 32 32'
                                        fill='currentColor'
                                        width='20'
                                        height='20'>
                                        <polygon points='22 16 12 26 10.6 24.6 19.2 16 10.6 7.4 12 6' />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                    <h3
                        className={`mb-1 font-['Effra'] text-base leading-5 font-medium text-[var(--colour-charcoal-dark)] ${totalEvents > 1 ? '' : 'mr-5'}`}
                        dangerouslySetInnerHTML={{ __html: activeEvent?.title ?? '' }}
                    />
                    <div className={`font-['Effra'] text-sm leading-5 font-normal text-[var(--colour-charcoal-light)]`}>
                        {activeEvent?.time_description}
                    </div>
                    <div className={`font-['Effra'] text-sm leading-5 font-normal text-[var(--colour-charcoal-light)]`}>
                        {activeEvent?.location}
                    </div>
                    <p
                        className={`mt-1 mb-3 font-['Effra'] text-sm leading-5 font-normal text-[var(--colour-charcoal-light)]`}>
                        {!isMobile && activeEvent?.content && <>{activeEvent?.content}</>}
                    </p>
                    {activeEvent && (
                        <Bookmark
                            event={activeEvent}
                            state={state}
                            api={api}
                            actions={actions}
                        />
                    )}
                </div>
            </ErrorBoundary>
        );
    }, [activeEvent, popupEvents, user, itinerary, api, actions, fallbackRender, setActiveEvent]);

    useEffect(() => {
        if (!map.current) return;

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = window.setTimeout(() => {
            if (!map.current) return;

            if (viewMode === 'itinerary' || viewMode === 'events') {
                // Fit bounds to all filtered events when they change
                if (filteredEvents && filteredEvents.length > 0) {
                    const bounds = new (window as any).Mazemap.LngLatBounds();
                    let hasValidBounds = false;

                    filteredEvents.forEach((event) => {
                        if (!event.location_data) return;
                        const lat = parseFloat(event.location_data.center[1]);
                        const lng = parseFloat(event.location_data.center[0]);

                        if (!isNaN(lat) && !isNaN(lng)) {
                            bounds.extend({ lng, lat });
                            hasValidBounds = true;
                        }
                    });

                    if (hasValidBounds && prevViewModeRef.current !== viewMode) {
                        setActiveAmenity(null);
                        setActiveEvent(null);
                        setAmenityPopup(null);
                        map.current.fitBounds(bounds as any, { padding: 50, maxZoom: 16 });
                    }
                }
            }

            prevViewModeRef.current = viewMode;
        }, 300);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
        };
    }, [viewMode, filteredEvents]);

    useEffect(() => {
        if (!map.current) return;

        // Events View: Show active event
        if (!activeEvent) {
            if (popup.current) {
                popup.current.remove();
                popup.current = null;
            }

            if (marker.current) {
                marker.current.remove();
                marker.current = null;
            }

            prevActiveEventId.current = undefined;

            // Reset filter
            if (map.current.getLayer('unclustered-point')) {
                map.current.setFilter('unclustered-point', ['!has', 'point_count']);
            }
            return;
        }

        // Close any open amenity popups when an event is activated
        if (amenityPopup) {
            setAmenityPopup(null);
        }

        // Filter the unclustered-point layer to hide the active event marker
        if (map.current.getLayer('unclustered-point')) {
            map.current.setFilter('unclustered-point', ['all', ['!has', 'point_count'], ['!=', 'id', activeEvent.id]]);
        }

        // If the active event is the same, just update the popup content
        if (activeEvent.id === prevActiveEventId.current) {
            if (popup.current && (popup.current as any).isOpen()) {
                if (popupRoot.current) {
                    popupRoot.current.render(renderPopupContent());
                }
            }
            return;
        }

        // Different event or first time: clean up old objects
        if (popup.current) {
            popup.current.remove();
            popup.current = null;
        }

        if (marker.current) {
            marker.current.remove();
            marker.current = null;
        }

        if (!activeEvent.location_data || !activeEvent.location_data.center) {
            prevActiveEventId.current = undefined;
            return;
        }

        const latitude = parseFloat(activeEvent.location_data.center[1]);
        const longitude = parseFloat(activeEvent.location_data.center[0]);

        if (!isNaN(latitude) && !isNaN(longitude)) {
            // Find overlapping events
            const overlaps = filteredEventsRef.current.filter((e) => {
                if (!e.location_data?.center) return false;
                const eLat = parseFloat(e.location_data.center[1]);
                const eLng = parseFloat(e.location_data.center[0]);
                return Math.abs(eLat - latitude) < OVERLAP_THRESHOLD && Math.abs(eLng - longitude) < OVERLAP_THRESHOLD;
            });
            // Ensure the active event is in the list (in case distance check is slightly off due to float precision)
            if (!overlaps.find((e) => e.id === activeEvent.id)) {
                overlaps.push(activeEvent);
            }
            // Sort by title for consistent ordering
            overlaps.sort((a, b) => {
                return toPerthTimestamp(a) - toPerthTimestamp(b);
            });

            setPopupEvents(overlaps);

            const lngLat = {
                lng: longitude,
                lat: latitude,
            };

            const isMobile = window.innerWidth < 768;
            const flyToOffset = (isMobile ? [0, -120] : [0, 0]) as [number, number];

            map.current.easeTo({
                center: lngLat,
                zoom: map.current.getZoom(),
                offset: flyToOffset,
            });

            marker.current = new (window as any).Mazemap.MazeMarker({
                color: '#FFF',
                size: 20,
                innerCircle: true,
                innerCircleColor: activeEvent.marker_colour,
                innerCircleScale: 0.8,
                shape: 'circle',
            })
                .setLngLat(lngLat)
                .addTo(map.current);

            const popupContainer = document.createElement('div');
            popupRoot.current = createRoot(popupContainer);
            popupRoot.current.render(renderPopupContent());

            popup.current = new (window as any).Mazemap.Popup({
                closeOnClick: true,
                offset: [0, 0],
                maxWidth: isMobile ? '300px' : '400px',
                focusAfterOpen: false,
            }).setDOMContent(popupContainer);

            popup.current?.on('close', closePopup);

            marker.current?.setPopup(popup.current!);
            marker.current?.togglePopup();

            prevActiveEventId.current = activeEvent.id;
        }
    }, [activeEvent, renderPopupContent, setActiveEvent]);

    // Handle Active Event cleanup when it becomes null
    useEffect(() => {
        if (!activeEvent && map.current && map.current.getLayer('unclustered-point')) {
            map.current.setFilter('unclustered-point', ['!has', 'point_count']);
        }
    }, [activeEvent]);

    const eventsGeoJson = useMemo(() => {
        const activeEvents = filteredEvents.filter((event) => event.event_status !== 'ended');

        return {
            type: 'FeatureCollection',
            features: (activeEvents || [])
                .filter((event) => event.location_data && event.location_data.center)
                .map((event) => {
                    return {
                        type: 'Feature',
                        properties: {
                            id: event.id,
                            title: event.title,
                            colour: event.marker_colour,
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [
                                parseFloat(event.location_data.center[0]),
                                parseFloat(event.location_data.center[1]),
                            ],
                        },
                    };
                }),
        };
    }, [filteredEvents]);

    // Update the events GeoJSON source
    useEffect(() => {
        if (!map.current || !mazemapInitialised) return;
        const source = map.current.getSource('events') as mapboxgl.GeoJSONSource;
        if (source) {
            source.setData(eventsGeoJson as any);
        }
    }, [eventsGeoJson, mazemapInitialised]);

    // Combine POIs and Amenities into GeoJSON
    const amenityGeoJson = useMemo(() => {
        const features: any[] = [];

        selectedAmenityTypes.forEach((slug) => {
            const amenityType = amenityTypes.find((t) => t.slug === slug);
            if (!amenityType) return;

            // Handle Mazemap POI data if configured
            if (amenityType.poi && amenityType.poi.length > 0 && poiDataArray) {
                poiDataArray.forEach((response: any) => {
                    response.pois.forEach((poi: any) => {
                        // Check if any of the POI's types match the selected amenity type's POIs
                        const hasMatch = poi.types.some((t: any) => amenityType.poi.includes(t.poiTypeId));
                        if (hasMatch) {
                            features.push({
                                type: 'Feature',
                                geometry: poi.geometry,
                                properties: {
                                    id: poi.poiId,
                                    title: poi.title,
                                    content: `${poi.buildingName || ''} ${poi.floorName ? `<br>Level ${poi.floorName}` : ''}`,
                                    iconId: poi.types[0]?.iconId || amenityType.icon_id,
                                    type: slug,
                                    z: poi.z ?? 1,
                                },
                            });
                        }
                    });
                });
            }

            // Always merge with local amenities data for this slug
            const localAmenities = amenities.filter((a) => a.type === slug);
            localAmenities.forEach((amenity) => {
                const lat = parseFloat(amenity.location_data.center[1]);
                const lng = parseFloat(amenity.location_data.center[0]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    features.push({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [lng, lat],
                        },
                        properties: {
                            id: amenity.id,
                            title: amenity.title,
                            content: amenity.content,
                            icon: amenityType.icon,
                            ...(!amenityType.icon && { iconId: amenityType.icon_id }),
                            type: slug,
                            z: 1,
                        },
                    });
                }
            });
        });

        return {
            type: 'FeatureCollection',
            features,
        };
    }, [selectedAmenityTypes, amenityTypes, poiDataArray, amenities]);

    // Use Mazemap standard markers for amenities to ensure icons appear correctly
    useEffect(() => {
        if (!map.current) return;

        // Clear existing amenity markers
        amenityMarkersRef.current.forEach((m) => m.remove());
        amenityMarkersRef.current = [];
        amenityMarkerRootsRef.current.forEach((r) => r.unmount());
        amenityMarkerRootsRef.current = [];

        // clear popups
        setAmenityPopup(null);

        const bounds = new (window as any).Mazemap.LngLatBounds();
        let hasValidBounds = false;

        const m = map.current;

        amenityGeoJson.features.forEach((feature) => {
            let coordinates = ((window as any).Mazemap.Util as any).getPoiLngLat(feature);

            const { title, content, iconId, icon, type, id } = feature.properties;
            // const z = feature.properties.z || 1;

            const el = document.createElement('div');
            el.className = 'amenity-marker-container';
            el.style.cursor = 'pointer';
            el.title = title;

            // Create marker element
            const markerEl = document.createElement('div');
            markerEl.className = 'amenity-marker-icon';
            markerEl.style.width = '32px';
            markerEl.style.height = '32px';
            markerEl.style.backgroundColor = '#ffffff';
            markerEl.style.borderRadius = '50%';
            markerEl.style.display = 'flex';
            markerEl.style.alignItems = 'center';
            markerEl.style.justifyContent = 'center';
            markerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
            markerEl.style.border = '1px solid #cccccc';

            if (iconId) {
                const img = document.createElement('img');
                img.src = `https://api.mazemap.com/api/icons/${iconId}.svg`;
                img.style.width = '24px';
                img.style.height = '24px';
                markerEl.appendChild(img);
            } else if (icon) {
                const root = createRoot(markerEl);
                root.render(
                    <Icon
                        name={icon}
                        size={24}
                    />,
                );
                amenityMarkerRootsRef.current.push(root);
            } else {
                markerEl.style.backgroundColor = '#007bff';
            }

            el.appendChild(markerEl);

            const amMarker = new (window as any).Mazemap.MazeMarker({
                element: el,
                zLevel: 1, // z: hardcoding to 1 as we arent doing levels at this stage
                preventClickBubble: true,
            })
                .setLngLat(coordinates)
                .addTo(m);

            bounds.extend(coordinates);
            hasValidBounds = true;

            // Click listener for popup
            el.onclick = (event) => {
                event.stopPropagation();
                setActiveEvent(null);
                setAmenityPopup({
                    lngLat: [coordinates.lng, coordinates.lat],
                    title,
                    content,
                    type,
                    id,
                });
            };

            amenityMarkersRef.current.push(amMarker);
        });

        if (hasValidBounds) {
            map.current.fitBounds(bounds as any, { padding: 50, maxZoom: 16 });
        }

        return () => {
            amenityMarkersRef.current.forEach((m) => m.remove());
            amenityMarkersRef.current = [];
            amenityMarkerRootsRef.current.forEach((r) => r.unmount());
            amenityMarkerRootsRef.current = [];
        };
    }, [amenityGeoJson, setActiveEvent, setAmenityPopup]);

    // Handle Amenity Popup
    useEffect(() => {
        if (!map.current) return;

        const cleanupPopup = () => {
            if (amenityPopupOpenTimeoutRef.current) {
                clearTimeout(amenityPopupOpenTimeoutRef.current);
                amenityPopupOpenTimeoutRef.current = null;
            }
            if (amenityPopupRoot.current) {
                setTimeout(() => {
                    amenityPopupRoot.current?.unmount(); // Use optional chaining for safety
                    amenityPopupRoot.current = null;
                }, 0);
            }
            if (amenityPopupRef.current) {
                amenityPopupRef.current.off('close');
                amenityPopupRef.current.remove();
                amenityPopupRef.current = null;
            }
        };

        if (!amenityPopup) {
            console.log('clean up');
            cleanupPopup();
            return;
        }

        const popupContent = (
            <div className='text-left text-sm'>
                <h3 className='mr-5 mb-1 text-base font-bold'>{amenityPopup.title}</h3>
                <div dangerouslySetInnerHTML={{ __html: amenityPopup.content }} />
                {amenityPopup.type === 'transport' && (
                    <SaveParkingButton
                        amenity={amenityPopup}
                        state={state} // This includes the itinerary
                        api={api}
                        actions={actions}
                    />
                )}
            </div>
        );

        // Clean up any existing popup first
        cleanupPopup();

        // allow time for popup to close
        amenityPopupOpenTimeoutRef.current = window.setTimeout(() => {
            if (!map.current) return;

            const popupContainer = document.createElement('div');
            amenityPopupRoot.current = createRoot(popupContainer);
            amenityPopupRoot.current.render(popupContent);

            const isMobile = window.innerWidth < 768;
            const newPopup = new (window as any).Mazemap.Popup({
                closeOnClick: true,
                offset: [0, -10],
                maxWidth: isMobile ? '300px' : '400px',
            })
                .setLngLat(amenityPopup.lngLat)
                .setDOMContent(popupContainer)
                .addTo(map.current);

            amenityPopupRef.current = newPopup;

            // --- GTM TRACKING START ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

            (window as any).dataCoreLayer.push({
                event: 'od_planner_map_click_poi',
                click_text: amenityPopup.title,
                click_class: amenityPopup.type,
            });
            // --- GTM TRACKING END ---

            (newPopup as any).on('close', () => {
                if (amenityPopup.id == activeAmenity) {
                    setActiveAmenity(null);
                }

                // Only clear the state if the popup being closed is currently set in state
                setAmenityPopup((current) => {
                    if (
                        current &&
                        current.lngLat[0] === amenityPopup.lngLat[0] &&
                        current.lngLat[1] === amenityPopup.lngLat[1]
                    ) {
                        return null;
                    }
                    return current;
                });
            });
            amenityPopupOpenTimeoutRef.current = null;
        }, 100);

        return cleanupPopup;
    }, [amenityPopup, itinerary]);

    useEffect(() => {
        if (!activeAmenity) {
            setAmenityPopup(null);
            return;
        }

        if (activeAmenity && amenityGeoJson) {
            amenityGeoJson.features.forEach((feature) => {
                if (feature.properties.id === activeAmenity) {
                    const coordinates = ((window as any).Mazemap.Util as any).getPoiLngLat(feature);

                    // buffer to allow for markers to populate
                    if (activeAmenityTimeoutRef.current) {
                        clearTimeout(activeAmenityTimeoutRef.current);
                    }
                    activeAmenityTimeoutRef.current = window.setTimeout(() => {
                        if (!map?.current) {
                            return;
                        }

                        const isMobile = window.innerWidth < 768;
                        const flyToOffset = (isMobile ? [0, -120] : [0, 0]) as [number, number];

                        map.current.easeTo({
                            center: coordinates,
                            zoom: map.current.getZoom(),
                            offset: flyToOffset,
                        });

                        setAmenityPopup({
                            lngLat: coordinates,
                            title: feature.properties.title,
                            content: feature.properties.content,
                            type: feature.properties.type,
                            id: feature.properties.id,
                        });
                        activeAmenityTimeoutRef.current = null;
                    }, 500);
                }
            });
        }

        return () => {
            if (activeAmenityTimeoutRef.current) {
                clearTimeout(activeAmenityTimeoutRef.current);
                activeAmenityTimeoutRef.current = null;
            }
        };
    }, [activeAmenity, amenityGeoJson, setAmenityPopup]);

    useEffect(() => {
        if (!map.current || !mapDiv.current) return;

        const resizeMap = () => {
            if (map.current) {
                map.current.resize();
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            // Use requestAnimationFrame to avoid "ResizeObserver loop limit exceeded" style errors in some contexts
            // and simply debouncing/throttling slightly
            requestAnimationFrame(resizeMap);
        });

        resizeObserver.observe(mapDiv.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [mazemapInitialised]); // Re-bind if map re-initialises

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            isDragging.current = true;
            startY.current = e.clientY;
            startRatio.current = splitRatio;
            e.currentTarget.setPointerCapture(e.pointerId);
        },
        [splitRatio],
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!isDragging.current) return;

            const currentClientY = e.clientY;

            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }

            rafId.current = requestAnimationFrame(() => {
                const deltaY = currentClientY - startY.current;
                const container = mapDiv.current?.closest('.grid');
                if (!container) return;

                const containerHeight = container.getBoundingClientRect().height;
                const deltaRatio = (deltaY / containerHeight) * 100;

                let newRatio = startRatio.current + deltaRatio;
                newRatio = Math.max(12, Math.min(90, newRatio));

                setSplitRatio(newRatio);
            });
        },
        [setSplitRatio],
    );

    const handleRelease = useCallback((e: React.PointerEvent) => {
        isDragging.current = false;
        if (rafId.current !== null) {
            cancelAnimationFrame(rafId.current);
            rafId.current = null;
        }
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    }, []);

    return (
        <div className='relative h-[100%] w-[100%]'>
            <div
                ref={mapDiv}
                className='map'></div>
            <AmenityFilter />

            <div className='absolute bottom-4 left-4 flex hidden gap-2 md:flex'>
                <button
                    className='group inline-flex h-9 cursor-pointer items-center justify-center gap-2.5 rounded-full border border-[var(--colour-charcoal-dark)] bg-[var(--colour-charcoal-dark)] py-2.5 pr-4 pl-[5px] transition-colors hover:bg-[var(--colour-gold)]'
                    onClick={() => {
                        setMapExpanded(!mapExpanded);
                        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
                        (window as any).dataCoreLayer.push({
                            event: 'od_planner_map_expand',
                            click_text: mapExpanded ? 'Collapse map' : 'Expand map',
                            click_classes: mapExpanded ? 'map-collapsed' : 'map-expanded',
                        });
                    }}>
                    {/* Icon Container */}
                    <div className='inline-flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full'>
                        {!mapExpanded ? (
                            <svg
                                width='17'
                                height='17'
                                viewBox='0 0 17 17'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M16.5 0.75V5.25C16.5 5.44891 16.421 5.63968 16.2803 5.78033C16.1397 5.92098 15.9489 6 15.75 6C15.5511 6 15.3603 5.92098 15.2197 5.78033C15.079 5.63968 15 5.44891 15 5.25V2.56031L10.2806 7.28063C10.1399 7.42136 9.94902 7.50042 9.75 7.50042C9.55098 7.50042 9.36011 7.42136 9.21937 7.28063C9.07864 7.13989 8.99958 6.94902 8.99958 6.75C8.99958 6.55098 9.07864 6.36011 9.21937 6.21937L13.9397 1.5H11.25C11.0511 1.5 10.8603 1.42098 10.7197 1.28033C10.579 1.13968 10.5 0.948912 10.5 0.75C10.5 0.551088 10.579 0.360322 10.7197 0.21967C10.8603 0.0790178 11.0511 0 11.25 0H15.75C15.9489 0 16.1397 0.0790178 16.2803 0.21967C16.421 0.360322 16.5 0.551088 16.5 0.75ZM6.21937 9.21937L1.5 13.9397V11.25C1.5 11.0511 1.42098 10.8603 1.28033 10.7197C1.13968 10.579 0.948912 10.5 0.75 10.5C0.551088 10.5 0.360322 10.579 0.21967 10.7197C0.0790178 10.8603 0 11.0511 0 11.25V15.75C0 15.9489 0.0790178 16.1397 0.21967 16.2803C0.360322 16.421 0.551088 16.5 0.75 16.5H5.25C5.44891 16.5 5.63968 16.421 5.78033 16.2803C5.92098 16.1397 6 15.9489 6 15.75C6 15.5511 5.92098 15.3603 5.78033 15.2197C5.63968 15.079 5.44891 15 5.25 15H2.56031L7.28063 10.2806C7.42136 10.1399 7.50042 9.94902 7.50042 9.75C7.50042 9.55098 7.42136 9.36011 7.28063 9.21937C7.13989 9.07864 6.94902 8.99958 6.75 8.99958C6.55098 8.99958 6.36011 9.07864 6.21937 9.21937Z'
                                    fill='white'
                                />
                            </svg>
                        ) : (
                            <svg
                                width='17'
                                height='17'
                                viewBox='0 0 17 17'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M16.7747 1.31988L11.9112 6.18234H14.6825C14.8874 6.18234 15.084 6.26375 15.2289 6.40867C15.3738 6.55359 15.4552 6.75014 15.4552 6.95508C15.4552 7.16002 15.3738 7.35657 15.2289 7.50149C15.084 7.6464 14.8874 7.72782 14.6825 7.72782H10.046C9.84109 7.72782 9.64454 7.6464 9.49962 7.50149C9.35471 7.35657 9.27329 7.16002 9.27329 6.95508V2.31865C9.27329 2.1137 9.35471 1.91715 9.49962 1.77224C9.64454 1.62732 9.84109 1.54591 10.046 1.54591C10.251 1.54591 10.4475 1.62732 10.5924 1.77224C10.7374 1.91715 10.8188 2.1137 10.8188 2.31865V5.08988L15.6812 0.226456C15.8262 0.0814585 16.0229 -4.04216e-09 16.2279 0C16.433 4.04216e-09 16.6297 0.0814585 16.7747 0.226456C16.9197 0.371453 17.0011 0.568111 17.0011 0.773168C17.0011 0.978225 16.9197 1.17488 16.7747 1.31988ZM6.95508 9.27329H2.31865C2.1137 9.27329 1.91715 9.35471 1.77224 9.49962C1.62732 9.64454 1.54591 9.84109 1.54591 10.046C1.54591 10.251 1.62732 10.4475 1.77224 10.5924C1.91715 10.7374 2.1137 10.8188 2.31865 10.8188H5.08988L0.226456 15.6812C0.0814585 15.8262 -4.04216e-09 16.0229 0 16.2279C4.04216e-09 16.433 0.0814585 16.6297 0.226456 16.7747C0.371453 16.9197 0.568111 17.0011 0.773168 17.0011C0.978225 17.0011 1.17488 16.9197 1.31988 16.7747L6.18234 11.9112V14.6825C6.18234 14.8874 6.26375 15.084 6.40867 15.2289C6.55359 15.3738 6.75014 15.4552 6.95508 15.4552C7.16002 15.4552 7.35657 15.3738 7.50149 15.2289C7.6464 15.084 7.72782 14.8874 7.72782 14.6825V10.046C7.72782 9.84109 7.6464 9.64454 7.50149 9.49962C7.35657 9.35471 7.16002 9.27329 6.95508 9.27329Z'
                                    fill='white'
                                />
                            </svg>
                        )}
                    </div>

                    {/* Text */}
                    <div className='text-sm leading-tight font-bold text-[var(--colour-white)]'>
                        {mapExpanded ? 'Collapse map' : 'Expand map'}
                    </div>
                </button>
                <a
                    href={
                        (window as any).openDayMapData?.map_url ||
                        'https://www.curtin.edu.au/open-day/wp-content/uploads/2026/02/6221OD_Open-Day_-A3-Map-2026_WEB.pdf'
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={() => {
                        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
                        (window as any).dataCoreLayer.push({
                            event: 'od_planner_button_click_download_map',
                            click_text: 'Download campus map pdf',
                            click_class: 'button-download-map',
                            value: 'Download campus map',
                        });
                    }}
                    className='group inline-flex h-9 cursor-pointer items-center justify-center gap-2.5 rounded-full border border-[var(--colour-gold)] bg-[var(--colour-white)] py-2.5 pr-4 pl-[5px] no-underline transition-colors hover:bg-gray-50'>
                    {/* Icon Container */}
                    <div className='inline-flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-[var(--colour-gold)]'>
                        {/* Download Icon SVG */}
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='text-Charcoal---dark h-3.5 w-3.5'>
                            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                            <polyline points='7 10 12 15 17 10' />
                            <line
                                x1='12'
                                x2='12'
                                y1='15'
                                y2='3'
                            />
                        </svg>
                    </div>

                    {/* Text */}
                    <div className='text-sm leading-tight font-bold text-[var(--colour-charcoal-dark)]'>
                        Download campus map pdf
                    </div>
                </a>
            </div>
            {/* Mobile Resizer */}
            <div
                className='absolute right-0 bottom-0 left-0 z-[40] inline-flex cursor-pointer flex-col items-center justify-center gap-5 self-stretch overflow-hidden rounded-tl-[20px] rounded-tr-[20px] border-t border-r border-l border-neutral-500 bg-neutral-100 px-[5px] pt-1.5 pb-2 md:hidden'
                style={{ touchAction: 'none' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handleRelease}
                onPointerCancel={handleRelease}
                onLostPointerCapture={handleRelease}>
                <div className='inline-flex h-7 items-center justify-start gap-2.5 p-2.5'>
                    {/* Resizer Icon */}
                    <div className='flex h-6 w-5 items-center justify-center gap-2.5 rounded-[60px] bg-[var(--colour-charcoal-dark)] px-1.5 py-[3px]'>
                        <div className='h-3.5 w-3 bg-[var(--colour-charcoal-dark)]'>
                            <svg
                                width='10'
                                height='14'
                                viewBox='0 0 10 14'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <g clip-path='url(#clip0_4171_12620)'>
                                    <path
                                        d='M9.10421 3.91871C9.18521 3.99971 9.24946 4.09587 9.2933 4.20171C9.33713 4.30754 9.3597 4.42097 9.3597 4.53552C9.3597 4.65007 9.33713 4.7635 9.2933 4.86933C9.24946 4.97516 9.18521 5.07132 9.10421 5.15232C9.02321 5.23332 8.92705 5.29758 8.82121 5.34141C8.71538 5.38525 8.60195 5.40781 8.4874 5.40781C8.37285 5.40781 8.25942 5.38525 8.15359 5.34141C8.04776 5.29758 7.9516 5.23332 7.87059 5.15232L5.00016 2.2808L2.12973 5.15232C1.96614 5.31591 1.74427 5.40781 1.51292 5.40781C1.28157 5.40781 1.0597 5.31591 0.896114 5.15232C0.732527 4.98874 0.640625 4.76687 0.640625 4.53552C0.640625 4.30417 0.732527 4.0823 0.896114 3.91871L4.38336 0.431471C4.46432 0.350413 4.56047 0.28611 4.66631 0.242237C4.77215 0.198364 4.88559 0.175781 5.00016 0.175781C5.11473 0.175781 5.22818 0.198364 5.33401 0.242237C5.43985 0.28611 5.536 0.350413 5.61697 0.431471L9.10421 3.91871ZM9.10421 8.94143C9.18527 9.02239 9.24957 9.11854 9.29344 9.22438C9.33732 9.33022 9.3599 9.44366 9.3599 9.55823C9.3599 9.6728 9.33732 9.78625 9.29344 9.89208C9.24957 9.99792 9.18527 10.0941 9.10421 10.175L5.61697 13.6623C5.536 13.7433 5.43985 13.8076 5.33401 13.8515C5.22818 13.8954 5.11473 13.918 5.00016 13.918C4.88559 13.918 4.77215 13.8954 4.66631 13.8515C4.56047 13.8076 4.46432 13.7433 4.38336 13.6623L0.896114 10.175C0.732527 10.0115 0.640625 9.78958 0.640625 9.55823C0.640625 9.32688 0.732527 9.10501 0.896114 8.94143C1.0597 8.77784 1.28157 8.68594 1.51292 8.68594C1.74427 8.68594 1.96614 8.77784 2.12973 8.94143L5.00016 11.813L7.87059 8.94143C7.95156 8.86037 8.04771 8.79606 8.15355 8.75219C8.25939 8.70832 8.37283 8.68574 8.4874 8.68574C8.60197 8.68574 8.71542 8.70832 8.82125 8.75219C8.92709 8.79606 9.02324 8.86037 9.10421 8.94143Z'
                                        fill='#F4F4F4'
                                    />
                                </g>
                                <defs>
                                    <clipPath id='clip0_4171_12620'>
                                        <rect
                                            width='10'
                                            height='14'
                                            fill='white'
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Map;
